import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import { AdminLayout } from "@/pages/AdminDashboard";
import api from "@/services/api";
import { toast } from "sonner";
import {
  Mail,
  Server,
  Lock,
  CheckCircle,
  AlertCircle,
  Eye,
  EyeOff,
  MessageCircle,
  Key,
  Plus,
  Trash2
} from "lucide-react";

const DEFAULT_OTP_URL = "https://otpmore.hirelan.com/api";

export default function AdminSettings() {
  const { token } = useAuth();
  const [smtpSettings, setSmtpSettings] = useState({
    host: "",
    port: 587,
    username: "",
    password: "",
    from_email: "",
    from_name: "Inventive Pages",
    use_tls: true
  });
  const [otpProvider, setOtpProvider] = useState("otpmore");
  const [otpSettings, setOtpSettings] = useState({
    otp_api_url: DEFAULT_OTP_URL,
    otp_api_key: "",
    otp_api_secret: ""
  });
  const [interaktSettings, setInteraktSettings] = useState({
    interakt_secret_key: "",
    interakt_template_name: "",
    interakt_template_language: "en"
  });
  const [otpExtraKeys, setOtpExtraKeys] = useState([{ name: "", value: "" }]);
  const [loading, setLoading] = useState(true);
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [savingOtp, setSavingOtp] = useState(false);
  const [savingInterakt, setSavingInterakt] = useState(false);
  const [savingProvider, setSavingProvider] = useState(false);
  const [smtpConfigured, setSmtpConfigured] = useState(false);
  const [otpConfigured, setOtpConfigured] = useState(false);
  const [interaktConfigured, setInteraktConfigured] = useState(false);
  const [otpHasKey, setOtpHasKey] = useState(false);
  const [otpHasSecret, setOtpHasSecret] = useState(false);
  const [interaktHasSecret, setInteraktHasSecret] = useState(false);
  const [otpKeyHint, setOtpKeyHint] = useState("");
  const [otpSecretHint, setOtpSecretHint] = useState("");
  const [interaktSecretHint, setInteraktSecretHint] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showOtpKey, setShowOtpKey] = useState(false);
  const [showOtpSecret, setShowOtpSecret] = useState(false);
  const [showInteraktSecret, setShowInteraktSecret] = useState(false);
  const [showExtraValues, setShowExtraValues] = useState({});

  const fetchSettings = useCallback(async () => {
    if (!token) return;
    try {
      const response = await api.getSettings(token);
      if (!response.error) {
        setSmtpConfigured(!!response.configured);
        setSmtpSettings((prev) => ({
          ...prev,
          host: response.host || "",
          port: response.port || 587,
          username: response.username || "",
          from_email: response.from_email || "",
          from_name: response.from_name || "Inventive Pages",
          use_tls: response.use_tls !== false
        }));

        setOtpConfigured(!!response.otp_configured);
        setOtpHasKey(!!response.otp_has_key);
        setOtpHasSecret(!!response.otp_has_secret);
        setOtpKeyHint(response.otp_key_hint || "");
        setOtpSecretHint(response.otp_secret_hint || "");
        setOtpProvider(response.otp_provider || "otpmore");
        setOtpSettings((prev) => ({
          ...prev,
          otp_api_url: response.otp_api_url || DEFAULT_OTP_URL,
          otp_api_key: "",
          otp_api_secret: ""
        }));

        setInteraktConfigured(!!response.interakt_configured);
        setInteraktHasSecret(!!response.interakt_has_secret);
        setInteraktSecretHint(response.interakt_secret_hint || "");
        setInteraktSettings({
          interakt_secret_key: "",
          interakt_template_name: response.interakt_template_name || "",
          interakt_template_language: response.interakt_template_language || "en"
        });
        const extras = response.otp_extra_keys?.length
          ? response.otp_extra_keys
          : [{ name: "", value: "" }];
        setOtpExtraKeys(extras);
      }
    } catch (error) {
      console.error("Failed to fetch settings");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const saveSmtpSettings = async (e) => {
    e.preventDefault();
    setSavingSmtp(true);

    try {
      const response = await api.saveSettings(smtpSettings, token);
      if (response.error) {
        toast.error(response.error);
      } else {
        setSmtpConfigured(true);
        toast.success("SMTP settings saved successfully");
      }
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSavingSmtp(false);
    }
  };

  const saveOtpProvider = async (provider) => {
    setSavingProvider(true);
    try {
      const response = await api.saveSettings({ otp_provider: provider }, token);
      if (response.error) {
        toast.error(response.error);
      } else {
        setOtpProvider(provider);
        toast.success(`OTP provider set to ${provider === "interakt" ? "Interakt" : "OTPMore"}`);
      }
    } catch (error) {
      toast.error("Failed to update OTP provider");
    } finally {
      setSavingProvider(false);
    }
  };

  const saveInteraktSettings = async (e) => {
    e.preventDefault();
    setSavingInterakt(true);

    try {
      const payload = {
        interakt_template_name: interaktSettings.interakt_template_name.trim(),
        interakt_template_language: interaktSettings.interakt_template_language.trim() || "en"
      };
      if (interaktSettings.interakt_secret_key.trim()) {
        payload.interakt_secret_key = interaktSettings.interakt_secret_key.trim();
      }

      if (!interaktHasSecret && !payload.interakt_secret_key) {
        toast.error("Interakt Secret Key is required");
        setSavingInterakt(false);
        return;
      }
      if (!payload.interakt_template_name) {
        toast.error("Authentication template name is required");
        setSavingInterakt(false);
        return;
      }

      const response = await api.saveSettings(payload, token);
      if (response.error) {
        toast.error(response.error);
      } else {
        setInteraktConfigured(true);
        setInteraktHasSecret(true);
        setInteraktSettings((prev) => ({ ...prev, interakt_secret_key: "" }));
        toast.success("Interakt settings saved successfully");
        fetchSettings();
      }
    } catch (error) {
      toast.error("Failed to save Interakt settings");
    } finally {
      setSavingInterakt(false);
    }
  };

  const saveOtpSettings = async (e) => {
    e.preventDefault();
    setSavingOtp(true);

    try {
      const payload = {
        otp_api_url: otpSettings.otp_api_url,
        otp_extra_keys: otpExtraKeys.filter((k) => k.name.trim())
      };
      if (otpSettings.otp_api_key.trim()) {
        payload.otp_api_key = otpSettings.otp_api_key.trim();
      }
      if (otpSettings.otp_api_secret.trim()) {
        payload.otp_api_secret = otpSettings.otp_api_secret.trim();
      }

      if (!otpHasKey && !payload.otp_api_key) {
        toast.error("API Key is required");
        setSavingOtp(false);
        return;
      }
      if (!otpHasSecret && !payload.otp_api_secret) {
        toast.error("API Secret is required");
        setSavingOtp(false);
        return;
      }

      const response = await api.saveSettings(payload, token);
      if (response.error) {
        toast.error(response.error);
      } else {
        setOtpConfigured(true);
        setOtpHasKey(true);
        setOtpHasSecret(true);
        setOtpSettings((prev) => ({ ...prev, otp_api_key: "", otp_api_secret: "" }));
        toast.success("OTPMore settings saved successfully");
        fetchSettings();
      }
    } catch (error) {
      toast.error("Failed to save OTPMore settings");
    } finally {
      setSavingOtp(false);
    }
  };

  const addExtraKey = () => {
    setOtpExtraKeys([...otpExtraKeys, { name: "", value: "" }]);
  };

  const updateExtraKey = (index, field, value) => {
    setOtpExtraKeys((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const removeExtraKey = (index) => {
    setOtpExtraKeys((prev) => {
      if (prev.length === 1) return [{ name: "", value: "" }];
      return prev.filter((_, i) => i !== index);
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#c4646a]"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 md:p-8 space-y-6" data-testid="admin-settings">
        <div>
          <h1 className="text-3xl font-light text-white">Settings</h1>
          <p className="text-gray-400 mt-1">Configure system settings</p>
        </div>

        {/* SMTP Settings */}
        <div className="bg-white/5 border border-white/10 rounded-xl">
          <div className="p-6 border-b border-white/10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#c4646a]/20 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-[#c4646a]" />
                </div>
                <div>
                  <h2 className="text-lg font-medium text-white">Email Configuration</h2>
                  <p className="text-gray-400 text-sm">SMTP settings for sending Wedding Brief emails</p>
                </div>
              </div>
              {smtpConfigured ? (
                <span className="flex items-center gap-2 text-green-400 text-sm">
                  <CheckCircle className="w-4 h-4" />
                  Configured
                </span>
              ) : (
                <span className="flex items-center gap-2 text-yellow-400 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  Not Configured
                </span>
              )}
            </div>
          </div>

          <form onSubmit={saveSmtpSettings} className="p-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm text-gray-400 mb-2">SMTP Host</label>
                <div className="relative">
                  <Server className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    type="text"
                    value={smtpSettings.host}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, host: e.target.value })}
                    className="luxury-input pl-12"
                    placeholder="smtp.gmail.com"
                    required
                    data-testid="input-smtp-host"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">SMTP Port</label>
                <input
                  type="number"
                  value={smtpSettings.port}
                  onChange={(e) => setSmtpSettings({ ...smtpSettings, port: parseInt(e.target.value, 10) })}
                  className="luxury-input"
                  placeholder="587"
                  required
                  data-testid="input-smtp-port"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Username</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    type="text"
                    value={smtpSettings.username}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, username: e.target.value })}
                    className="luxury-input pl-12"
                    placeholder="your-email@gmail.com"
                    required
                    data-testid="input-smtp-username"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Password / App Password</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={smtpSettings.password}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, password: e.target.value })}
                    className="luxury-input pl-12 pr-12"
                    placeholder="••••••••••••"
                    required={!smtpConfigured}
                    data-testid="input-smtp-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className="text-gray-500 text-xs mt-1">For Gmail, use an App Password</p>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">From Email</label>
                <input
                  type="email"
                  value={smtpSettings.from_email}
                  onChange={(e) => setSmtpSettings({ ...smtpSettings, from_email: e.target.value })}
                  className="luxury-input"
                  placeholder="hello@inventivepages.com"
                  required
                  data-testid="input-from-email"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">From Name</label>
                <input
                  type="text"
                  value={smtpSettings.from_name}
                  onChange={(e) => setSmtpSettings({ ...smtpSettings, from_name: e.target.value })}
                  className="luxury-input"
                  placeholder="Inventive Pages"
                  required
                  data-testid="input-from-name"
                />
              </div>

              <div className="md:col-span-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={smtpSettings.use_tls}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, use_tls: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-600 bg-white/5 text-[#c4646a] focus:ring-[#c4646a]"
                    data-testid="checkbox-tls"
                  />
                  <span className="text-gray-300">Use TLS/SSL encryption</span>
                </label>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-white/10">
              <button
                type="submit"
                disabled={savingSmtp}
                className="px-6 py-3 bg-[#c4646a] text-white rounded-lg hover:bg-[#a85459] transition-colors disabled:opacity-50"
                data-testid="btn-save-smtp"
              >
                {savingSmtp ? "Saving..." : "Save Email Settings"}
              </button>
            </div>
          </form>
        </div>

        {/* OTP Provider */}
        <div className="bg-white/5 border border-white/10 rounded-xl">
          <div className="p-6 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h2 className="text-lg font-medium text-white">OTP Provider</h2>
                <p className="text-gray-400 text-sm">Choose which service sends WhatsApp OTP on the wedding form</p>
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="grid md:grid-cols-2 gap-4">
              <button
                type="button"
                disabled={savingProvider}
                onClick={() => saveOtpProvider("otpmore")}
                className={`p-4 rounded-xl border text-left transition-colors ${
                  otpProvider === "otpmore"
                    ? "border-green-500/50 bg-green-500/10"
                    : "border-white/10 bg-white/5 hover:bg-white/10"
                }`}
                data-testid="btn-otp-provider-otpmore"
              >
                <p className="text-white font-medium">OTPMore</p>
                <p className="text-gray-400 text-sm mt-1">External OTP API with send & verify endpoints</p>
              </button>
              <button
                type="button"
                disabled={savingProvider}
                onClick={() => saveOtpProvider("interakt")}
                className={`p-4 rounded-xl border text-left transition-colors ${
                  otpProvider === "interakt"
                    ? "border-green-500/50 bg-green-500/10"
                    : "border-white/10 bg-white/5 hover:bg-white/10"
                }`}
                data-testid="btn-otp-provider-interakt"
              >
                <p className="text-white font-medium">Interakt</p>
                <p className="text-gray-400 text-sm mt-1">WhatsApp authentication templates via Interakt API</p>
              </button>
            </div>
            <p className="text-gray-500 text-xs mt-4">
              Active provider: <span className="text-gray-300">{otpProvider === "interakt" ? "Interakt" : "OTPMore"}</span>
            </p>
          </div>
        </div>

        {/* OTPMore Settings */}
        <div className="bg-white/5 border border-white/10 rounded-xl">
          <div className="p-6 border-b border-white/10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <h2 className="text-lg font-medium text-white">OTPMore Configuration</h2>
                  <p className="text-gray-400 text-sm">WhatsApp OTP API keys for the wedding form</p>
                </div>
              </div>
              {otpConfigured ? (
                <span className="flex items-center gap-2 text-green-400 text-sm">
                  <CheckCircle className="w-4 h-4" />
                  Configured
                </span>
              ) : (
                <span className="flex items-center gap-2 text-yellow-400 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  Not Configured
                </span>
              )}
            </div>
          </div>

          <form onSubmit={saveOtpSettings} className="p-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm text-gray-400 mb-2">API Base URL</label>
                <div className="relative">
                  <Server className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    type="url"
                    value={otpSettings.otp_api_url}
                    onChange={(e) => setOtpSettings({ ...otpSettings, otp_api_url: e.target.value })}
                    className="luxury-input pl-12"
                    placeholder={DEFAULT_OTP_URL}
                    required
                    data-testid="input-otp-url"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">API Key</label>
                <div className="relative">
                  <Key className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    type={showOtpKey ? "text" : "password"}
                    value={otpSettings.otp_api_key}
                    onChange={(e) => setOtpSettings({ ...otpSettings, otp_api_key: e.target.value })}
                    className="luxury-input pl-12 pr-12"
                    placeholder={otpHasKey ? otpKeyHint : "Enter API key"}
                    required={!otpHasKey}
                    data-testid="input-otp-key"
                  />
                  <button
                    type="button"
                    onClick={() => setShowOtpKey(!showOtpKey)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                  >
                    {showOtpKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {otpHasKey && (
                  <p className="text-gray-500 text-xs mt-1">Saved key {otpKeyHint}. Leave blank to keep current.</p>
                )}
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">API Secret</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    type={showOtpSecret ? "text" : "password"}
                    value={otpSettings.otp_api_secret}
                    onChange={(e) => setOtpSettings({ ...otpSettings, otp_api_secret: e.target.value })}
                    className="luxury-input pl-12 pr-12"
                    placeholder={otpHasSecret ? otpSecretHint : "Enter API secret"}
                    required={!otpHasSecret}
                    data-testid="input-otp-secret"
                  />
                  <button
                    type="button"
                    onClick={() => setShowOtpSecret(!showOtpSecret)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                  >
                    {showOtpSecret ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {otpHasSecret && (
                  <p className="text-gray-500 text-xs mt-1">Saved secret {otpSecretHint}. Leave blank to keep current.</p>
                )}
              </div>
            </div>

            <div className="mt-8">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-white font-medium">Additional API Keys</h3>
                  <p className="text-gray-500 text-sm">Add custom header keys sent with each OTP request</p>
                </div>
                <button
                  type="button"
                  onClick={addExtraKey}
                  className="flex items-center gap-2 px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg text-white hover:bg-white/10"
                  data-testid="btn-add-otp-key"
                >
                  <Plus className="w-4 h-4" />
                  Add Key
                </button>
              </div>

              <div className="space-y-3">
                {otpExtraKeys.map((item, index) => (
                  <div key={index} className="grid md:grid-cols-[1fr_1fr_auto] gap-3 items-start">
                    <input
                      type="text"
                      value={item.name}
                      onChange={(e) => updateExtraKey(index, "name", e.target.value)}
                      className="luxury-input py-3"
                      placeholder="Header name (e.g. X-Custom-Key)"
                      data-testid={`input-otp-extra-name-${index}`}
                    />
                    <div className="relative">
                      <input
                        type={showExtraValues[index] ? "text" : "password"}
                        value={item.value}
                        onChange={(e) => updateExtraKey(index, "value", e.target.value)}
                        className="luxury-input py-3 pr-12"
                        placeholder="Header value"
                        data-testid={`input-otp-extra-value-${index}`}
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setShowExtraValues((prev) => ({ ...prev, [index]: !prev[index] }))
                        }
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                      >
                        {showExtraValues[index] ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeExtraKey(index)}
                      className="p-3 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg"
                      title="Remove key"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-white/10">
              <button
                type="submit"
                disabled={savingOtp}
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-500 transition-colors disabled:opacity-50"
                data-testid="btn-save-otp"
              >
                {savingOtp ? "Saving..." : "Save OTPMore Settings"}
              </button>
            </div>
          </form>
        </div>

        {/* Interakt Settings */}
        <div className="bg-white/5 border border-white/10 rounded-xl">
          <div className="p-6 border-b border-white/10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <h2 className="text-lg font-medium text-white">Interakt Configuration</h2>
                  <p className="text-gray-400 text-sm">Secret Key and authentication template for WhatsApp OTP</p>
                </div>
              </div>
              {interaktConfigured ? (
                <span className="flex items-center gap-2 text-green-400 text-sm">
                  <CheckCircle className="w-4 h-4" />
                  Configured
                </span>
              ) : (
                <span className="flex items-center gap-2 text-yellow-400 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  Not Configured
                </span>
              )}
            </div>
          </div>

          <form onSubmit={saveInteraktSettings} className="p-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm text-gray-400 mb-2">Secret Key</label>
                <div className="relative">
                  <Key className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    type={showInteraktSecret ? "text" : "password"}
                    value={interaktSettings.interakt_secret_key}
                    onChange={(e) =>
                      setInteraktSettings({ ...interaktSettings, interakt_secret_key: e.target.value })
                    }
                    className="luxury-input pl-12 pr-12"
                    placeholder={interaktHasSecret ? interaktSecretHint : "Enter Interakt Secret Key"}
                    required={!interaktHasSecret}
                    data-testid="input-interakt-secret"
                  />
                  <button
                    type="button"
                    onClick={() => setShowInteraktSecret(!showInteraktSecret)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                  >
                    {showInteraktSecret ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {interaktHasSecret && (
                  <p className="text-gray-500 text-xs mt-1">
                    Saved key {interaktSecretHint}. Leave blank to keep current. Find it in Interakt → Settings → Developer Settings.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Authentication Template Name</label>
                <input
                  type="text"
                  value={interaktSettings.interakt_template_name}
                  onChange={(e) =>
                    setInteraktSettings({ ...interaktSettings, interakt_template_name: e.target.value })
                  }
                  className="luxury-input"
                  placeholder="e.g. verification_code"
                  required
                  data-testid="input-interakt-template"
                />
                <p className="text-gray-500 text-xs mt-1">Approved WhatsApp authentication template name from Interakt</p>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Template Language Code</label>
                <input
                  type="text"
                  value={interaktSettings.interakt_template_language}
                  onChange={(e) =>
                    setInteraktSettings({ ...interaktSettings, interakt_template_language: e.target.value })
                  }
                  className="luxury-input"
                  placeholder="en"
                  required
                  data-testid="input-interakt-language"
                />
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-white/10">
              <button
                type="submit"
                disabled={savingInterakt}
                className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-500 transition-colors disabled:opacity-50"
                data-testid="btn-save-interakt"
              >
                {savingInterakt ? "Saving..." : "Save Interakt Settings"}
              </button>
            </div>
          </form>
        </div>

        <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-6">
          <h3 className="text-blue-400 font-medium mb-2">Configuration Help</h3>
          <ul className="text-gray-400 text-sm space-y-2">
            <li>• <strong>Email:</strong> Use Gmail App Password with smtp.gmail.com on port 587.</li>
            <li>• <strong>OTPMore:</strong> Enter your API Key and Secret from the OTPMore dashboard.</li>
            <li>• <strong>Interakt:</strong> Use your Secret Key from Developer Settings and an approved authentication template name.</li>
            <li>• <strong>OTP Provider:</strong> Select OTPMore or Interakt — only the active provider is used on the wedding form.</li>
            <li>• <strong>Additional Keys:</strong> Use for extra HTTP headers if your OTP provider requires them.</li>
          </ul>
        </div>
      </div>
    </AdminLayout>
  );
}
